import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Star, Send, Check, Home, ThumbsUp, ThumbsDown,
  Calendar, MapPin, Clock, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../services/api';

const LeaveReview = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Review form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [serviceQuality, setServiceQuality] = useState(0);
  const [punctuality, setPunctuality] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [wouldRecommend, setWouldRecommend] = useState(true);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      // Get booking details
      const ref = searchParams.get('ref') || bookingId;
      const response = await api.get(`/bookings/lookup?ref=${ref}`);
      
      if (response.data.status !== 'completed') {
        setError('This booking is not yet completed. You can leave a review after the service is finished.');
        return;
      }
      
      setBooking(response.data);
    } catch (err) {
      setError('Booking not found. Please check your link and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reviews/', {
        booking_id: booking.id,
        rating,
        comment: comment.trim() || null,
        service_quality: serviceQuality || null,
        punctuality: punctuality || null,
        communication: communication || null,
        would_recommend: wouldRecommend
      });
      
      setSubmitted(true);
      toast.success('Thank you for your review!');
    } catch (err) {
      if (err.response?.data?.detail?.includes('already been reviewed')) {
        setError('This booking has already been reviewed. Thank you!');
      } else {
        toast.error('Failed to submit review. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const StarButton = ({ value, currentRating, onSelect, onHover, size = 'lg' }) => {
    const sizeClass = size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
    const isActive = value <= (hoverRating || currentRating);
    
    return (
      <button
        type="button"
        onClick={() => onSelect(value)}
        onMouseEnter={() => onHover?.(value)}
        onMouseLeave={() => onHover?.(0)}
        className="focus:outline-none transition-transform hover:scale-110"
      >
        <Star 
          className={`${sizeClass} transition-colors ${
            isActive ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/30 hover:text-white/50'
          }`}
        />
      </button>
    );
  };

  const MiniRating = ({ label, value, onChange }) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-white/70">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <StarButton 
            key={star} 
            value={star} 
            currentRating={value}
            onSelect={onChange}
            size="sm"
          />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-yellow-500" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Unable to Leave Review</h1>
          <p className="text-white/60 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#E6C965]">
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md"
        >
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-2">Thank You!</h1>
          <p className="text-white/60 mb-6">
            Your feedback helps us improve our services and helps other customers make informed decisions.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E6C965]">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white py-8 px-4" data-testid="leave-review-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Star className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Rate Your Experience</h1>
          <p className="text-white/60">Your feedback helps us serve you better</p>
        </div>

        {/* Booking Info */}
        {booking && (
          <div className="bg-[#1a1a1a] rounded-xl p-4 mb-6 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="font-medium">{booking.service_name || booking.service_type}</p>
                <p className="text-sm text-white/50">{booking.scheduled_date} at {booking.scheduled_time}</p>
              </div>
            </div>
            {booking.worker_name && (
              <div className="flex items-center gap-2 text-sm text-white/60">
                <User className="w-4 h-4" />
                <span>Service by {booking.worker_name}</span>
              </div>
            )}
          </div>
        )}

        {/* Main Rating */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 mb-4 border border-white/10">
          <p className="text-center text-white/70 mb-4">How was your overall experience?</p>
          <div className="flex justify-center gap-2 mb-2">
            {[1, 2, 3, 4, 5].map(star => (
              <StarButton
                key={star}
                value={star}
                currentRating={rating}
                onSelect={setRating}
                onHover={setHoverRating}
              />
            ))}
          </div>
          <p className="text-center text-sm text-[#D4AF37]">
            {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : rating === 1 ? 'Poor' : 'Tap to rate'}
          </p>
        </div>

        {/* Detailed Ratings */}
        {rating > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="bg-[#1a1a1a] rounded-xl p-4 mb-4 border border-white/10"
          >
            <p className="text-sm text-white/70 mb-3">Rate specific aspects (optional)</p>
            <MiniRating label="Service Quality" value={serviceQuality} onChange={setServiceQuality} />
            <MiniRating label="Punctuality" value={punctuality} onChange={setPunctuality} />
            <MiniRating label="Communication" value={communication} onChange={setCommunication} />
          </motion.div>
        )}

        {/* Comment */}
        {rating > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="bg-[#1a1a1a] rounded-xl p-4 mb-4 border border-white/10"
          >
            <p className="text-sm text-white/70 mb-3">Tell us more (optional)</p>
            <Textarea
              placeholder="Share your experience..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/40"
              rows={4}
            />
          </motion.div>
        )}

        {/* Would Recommend */}
        {rating > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="bg-[#1a1a1a] rounded-xl p-4 mb-6 border border-white/10"
          >
            <p className="text-sm text-white/70 mb-3">Would you recommend us to others?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setWouldRecommend(true)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border transition-colors ${
                  wouldRecommend 
                    ? 'bg-green-500/20 border-green-500 text-green-400' 
                    : 'border-white/10 text-white/50 hover:border-white/30'
                }`}
              >
                <ThumbsUp className="w-5 h-5" />
                Yes
              </button>
              <button
                onClick={() => setWouldRecommend(false)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border transition-colors ${
                  !wouldRecommend 
                    ? 'bg-red-500/20 border-red-500 text-red-400' 
                    : 'border-white/10 text-white/50 hover:border-white/30'
                }`}
              >
                <ThumbsDown className="w-5 h-5" />
                No
              </button>
            </div>
          </motion.div>
        )}

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={rating === 0 || submitting}
          className="w-full h-12 bg-[#D4AF37] text-black hover:bg-[#E6C965] disabled:opacity-50"
        >
          {submitting ? (
            <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Submit Review
            </>
          )}
        </Button>

        {/* Skip */}
        <Link to="/" className="block text-center mt-4">
          <span className="text-white/50 hover:text-white/70 text-sm">Skip for now</span>
        </Link>
      </div>
    </div>
  );
};

export default LeaveReview;
